<template><div><h1 id="making-a-bootable-usb" tabindex="-1"><a class="header-anchor" href="#making-a-bootable-usb"><span>Making a Bootable USB</span></a></h1>
<p>Creating a bootable USB drive is essential for installing an operating system on your Chromebook/Chromebox after flashing custom firmware, or for creating a ChromeOS recovery USB to restore stock firmware.</p>
<p>This guide covers creating bootable USB drives for Linux and Windows installation. For creating a ChromeOS recovery USB, see <RouteLink to="/docs/reverting/recovery-usb.html">ChromeOS Recovery USB</RouteLink>.</p>
<div class="hint-container caution">
<p class="hint-container-title">DATA LOSS WARNING</p>
<p>Flashing Ventoy or ISOs to an external drive will <strong>wipe all data</strong> on that drive. Back up any important files before proceeding.</p>
</div>
<h2 id="prerequisites" tabindex="-1"><a class="header-anchor" href="#prerequisites"><span>Prerequisites</span></a></h2>
<ol>
<li>Another PC or laptop (Windows, Linux, or macOS)</li>
<li>An internet connection to download ISO files and tools</li>
<li>A USB drive or SD card with at least 8GB capacity (16GB+ recommended for Windows)</li>
</ol>
<h2 id="downloading-an-iso" tabindex="-1"><a class="header-anchor" href="#downloading-an-iso"><span>Downloading an ISO</span></a></h2>
<ol>
<li>
<p>Determine what OS you want.</p>
<p><strong>For Linux:</strong></p>
<ul>
<li>Most modern Linux distributions work well with MrChromebox firmware</li>
<li>For best results, use distributions with recent kernels (6.1+)</li>
<li>See the <a href="https://docs.chrultrabook.com/docs/devices.html" target="_blank" rel="noopener noreferrer">chrultrabook Supported Devices page</a> for specific OS compatibility by device</li>
</ul>
<p><strong>For Windows:</strong></p>
<ul>
<li>Only official versions of Windows 10 (version 1709 or later) and Windows 11 are supported</li>
<li>Driver support varies by device - check <a href="https://docs.chrultrabook.com/docs/devices.html" target="_blank" rel="noopener noreferrer">chrultrabook compatibility</a> before installing</li>
</ul>
</li>
<li>
<p>Place the ISO in a safe place.</p>
</li>
</ol>
<h2 id="flashing-using-rufus-windows" tabindex="-1"><a class="header-anchor" href="#flashing-using-rufus-windows"><span>Flashing using Rufus (Windows)</span></a></h2>
<p>For this guide, we will be using Rufus to create our bootable USB. Rufus is a useful tool that allows you to flash ISOs to an external drive.</p>
<ol>
<li>
<p>Download Rufus from <a href="https://rufus.ie/en/" target="_blank" rel="noopener noreferrer">here</a>. The standard Windows x64 version will do.</p>
<p><img src="/rufus.png" alt="image"></p>
</li>
<li>
<p>Plug in your external drive.</p>
</li>
<li>
<p>Locate and launch Rufus. It should ask you to launch with administrator privileges, select YES.</p>
</li>
<li>
<p>Press the &quot;SELECT&quot; button on the right-hand side.</p>
</li>
<li>
<p>A File Explorer window should pop up, find your ISO of choice, then click &quot;Open&quot;.</p>
</li>
<li>
<p>Click &quot;Start&quot;. The default options should work just fine.</p>
</li>
<li>
<p>Wait for the green bar to say &quot;READY&quot;, then click &quot;Close&quot; and eject your newly made external drive.</p>
</li>
<li>
<p>Profit</p>
</li>
</ol>
<h2 id="flashing-using-ventoy-windows-linux-macos" tabindex="-1"><a class="header-anchor" href="#flashing-using-ventoy-windows-linux-macos"><span>Flashing using Ventoy (Windows/Linux/macOS)</span></a></h2>
<p>For this guide, we will be using Ventoy to create our bootable USB.
Ventoy allows you to boot multiple ISO's from a single drive.</p>
<ol>
<li>
<p>Download and extract the latest version from <a href="https://www.ventoy.net/en/download.html" target="_blank" rel="noopener noreferrer">Github</a>.</p>
<ul>
<li>Download the respective file for your platform.
<img src="/ventoy/download-alt.png" alt="image"></li>
</ul>
</li>
<li>
<p>Run <code v-pre>Ventoy2Disk.exe</code> for Windows or <code v-pre>VentoyGUI.x86_64</code> for Linux.</p>
<ul>
<li>Make sure to allow administrator privileges when prompted.</li>
</ul>
</li>
<li>
<p>Plug in your external drive and click the green circle to refresh devices. It should pick up your external drive.</p>
</li>
<li>
<p>Click Options &gt; Partition Style and select &quot;GPT&quot;.</p>
</li>
<li>
<p>Once you have done Step 4, click &quot;Install&quot; and click ok twice to start the install.</p>
</li>
<li>
<p>Open your Files App and find a drive that's labeled as &quot;Ventoy&quot;.</p>
</li>
<li>
<p>Copy the ISO you downloaded into that drive, after that eject it.</p>
</li>
<li>
<p>Profit</p>
</li>
</ol>
<h2 id="flashing-with-dd-linux-macos" tabindex="-1"><a class="header-anchor" href="#flashing-with-dd-linux-macos"><span>Flashing with dd (Linux/macOS)</span></a></h2>
<ol>
<li>Plug in your external drive</li>
<li>Run <code v-pre>lsblk</code> to help determine the drive to be flashed. If the device is mounted you will need to use <code v-pre>umount</code> to unmount it.</li>
<li>Flash with <code v-pre>dd if=myfile.iso of=/dev/&lt;device name (as shown by lsblk)&gt; bs=16M status=progress</code>.</li>
</ol>
<div class="hint-container tip">
<p class="hint-container-title">Tips</p>
<p>Be sure to target the drive itself and not a partition (e.g., <code v-pre>/dev/sdb</code>, not <code v-pre>/dev/sdb1</code>).</p>
</div>
<h2 id="next-steps" tabindex="-1"><a class="header-anchor" href="#next-steps"><span>Next Steps</span></a></h2>
<p>After creating your bootable USB:</p>
<p><strong>For installing an OS on your device:</strong></p>
<ol>
<li>Insert the USB drive into your Chromebook/Chromebox</li>
<li>Power on and access the boot menu:
<ul>
<li><strong>Stock firmware with RW_LEGACY</strong>: Press <code v-pre>CTRL+L</code> in Developer Mode, then <code v-pre>ESC</code> to access boot menu</li>
<li><strong>UEFI Full ROM firmware</strong>: Press <code v-pre>ESC</code> during boot to access UEFI setup menu, select &quot;Boot Manager&quot;</li>
</ul>
</li>
<li>Select your USB drive from the boot menu</li>
<li>Follow the OS installation instructions</li>
</ol>
<p><strong>For reverting to stock firmware:</strong></p>
<ul>
<li>See <RouteLink to="/docs/reverting/flashing-stock.html">Restoring Stock Firmware</RouteLink> for complete instructions</li>
<li>You'll need either a firmware backup or a ChromeOS recovery USB</li>
</ul>
<h2 id="additional-resources" tabindex="-1"><a class="header-anchor" href="#additional-resources"><span>Additional Resources</span></a></h2>
<ul>
<li><RouteLink to="/docs/firmware/booting.html">Booting from USB/SD Card</RouteLink> - Understanding boot modes and boot order</li>
<li><RouteLink to="/docs/reverting/flashing-stock.html">Restoring Stock Firmware</RouteLink> - Reverting to ChromeOS</li>
<li><a href="https://docs.chrultrabook.com/" target="_blank" rel="noopener noreferrer">chrultrabook Documentation</a> - OS installation guides and compatibility information</li>
</ul>
</div></template>
<script>
export const _pageData = JSON.parse("{\"path\":\"/docs/support/bootableusb.html\",\"title\":\"Making a Bootable USB\",\"lang\":\"en-US\",\"frontmatter\":{},\"git\":{\"updatedTime\":1764108211000,\"contributors\":[{\"name\":\"Matt DeVillier\",\"username\":\"\",\"email\":\"matt.devillier@gmail.com\",\"commits\":5},{\"name\":\"appleflyerv3\",\"username\":\"appleflyerv3\",\"email\":\"164886846+appleflyerv3@users.noreply.github.com\",\"commits\":1,\"url\":\"https://github.com/appleflyerv3\"}],\"changelog\":[{\"hash\":\"3b9581612ed009e8df1eb8a5e95af2d55961706f\",\"time\":1764108211000,\"email\":\"matt.devillier@gmail.com\",\"author\":\"Matt DeVillier\",\"message\":\"docs: consolidate ChromeOS recovery documentation\"},{\"hash\":\"bd4c8b1ae1ae0da065119b886e7aca9135e24362\",\"time\":1764107992000,\"email\":\"matt.devillier@gmail.com\",\"author\":\"Matt DeVillier\",\"message\":\"docs: relocate bootable USB guide to support section\"},{\"hash\":\"fe6abda174655d5ca5cfc388c4eb426f0f94f65e\",\"time\":1764105380000,\"email\":\"matt.devillier@gmail.com\",\"author\":\"Matt DeVillier\",\"message\":\"bootableusb: improve guide with better context\"},{\"hash\":\"07a47ba73cd79b3d58e4fdf19ac4e99ebe5aa28a\",\"time\":1739123383000,\"email\":\"164886846+appleflyerv3@users.noreply.github.com\",\"author\":\"appleflyer\",\"message\":\"fix some typos (#33)\"},{\"hash\":\"31754e9a539127b24596a6ef24c63c25693b74b1\",\"time\":1720622740000,\"email\":\"matt.devillier@gmail.com\",\"author\":\"Matt DeVillier\",\"message\":\"project-wide: trim all trailing whitespace\"},{\"hash\":\"39a8e8685b2be911b99ae30f8f0c7fb6234b5e50\",\"time\":1719969991000,\"email\":\"matt.devillier@gmail.com\",\"author\":\"Matt DeVillier\",\"message\":\"WIP content update\"}]},\"filePathRelative\":\"docs/support/bootableusb.md\"}")
export default { name: "bootableusb.md" }
</script>